/**
 * Public CV and web card URLs.
 *
 * A public CV lives at /cv/<slug> and its web card at /web/<slug>, the slug coming
 * from the name on the CV (supabase/migrations/20261007_cv_public_slug.sql).
 * The slug only exists once the CV has been made public; until then the pages are
 * reached by the row's UUID under /cv/public/ and /card/public/, the old URLs,
 * which redirect to the short ones once there is a slug
 * (server/middleware/publicCvRedirect.ts). /cv/<uuid> itself is the editor.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export type PublicCvKind = 'cv' | 'card'

export function isCvUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

/** The path segment a CV is shared under: its slug when it has one, else its id. */
export function publicCvKey(cv: { id: string, public_url_slug?: string | null }): string {
  return cv.public_url_slug || cv.id
}

export function publicCvPath(kind: PublicCvKind, cv: { id: string, public_url_slug?: string | null }): string {
  if (cv.public_url_slug) {
    return kind === 'cv' ? `/cv/${cv.public_url_slug}` : `/web/${cv.public_url_slug}`
  }
  return kind === 'cv' ? `/cv/public/${cv.id}` : `/card/public/${cv.id}`
}

/** /cv/<slug>: one segment under /cv/ that is neither the editor (a UUID) nor a page of its own. */
export function isPublicCvSlugPath(path: string): boolean {
  const match = /^\/cv\/([^/]+)\/?$/.exec(path)
  return Boolean(match && !isCvUuid(match[1]) && match[1] !== 'new' && match[1] !== 'public')
}
